import CheckIcon from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Alert, {type AlertProps} from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Snackbar, {type SnackbarCloseReason, type SnackbarOrigin} from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import type * as React from 'react';
import IconButton from '../../Inputs/IconButton';

/** MUI Alert variants plus `paper` (bordered surface for promotional / feed banners). */
export type SnackbarAlertVariant = NonNullable<AlertProps['variant']> | 'paper';

export interface SnackbarAlertProps {
  alertText?: string;
  bodyHtml?: string;
  itemStatus: AlertProps['severity'];
  onClose: () => void;
  action?: () => void;
  actionLabel?: string;
  autoHideDuration?: number;
  anchorOrigin?: SnackbarOrigin;
  variant?: SnackbarAlertVariant;
  sx?: AlertProps['sx'];
  checkboxLabel?: string;
  checkboxChecked?: boolean;
  onCheckboxChange?: (checked: boolean) => void;
}

const styles = {
  snackbar: {
    mt: -1.5,
  }, // Align snackbar with AppBar
  alert: {
    boxShadow: 16,
    borderRadius: 2,
    alignItems: 'stretch',
    '& .MuiAlert-icon': {alignItems: 'flex-start', pt: 1},
    '& .MuiAlert-message': {
      flex: 1,
      minWidth: 0,
      py: 1,
      lineHeight: 1,
    },
    '& .MuiAlert-action': {
      display: 'flex',
      p: '0 0.5 0.5 1',
      mr: 0,
      alignSelf: 'stretch',
      alignItems: 'stretch',
    },
  },
  alertFilled: {
    color: 'common.white',
  },
  alertPaper: {
    minWidth: '600px',
    color: 'text.primary',
    backgroundColor: 'background.paper',
    border: 1,
    borderColor: 'divider',
    '&.MuiAlert-standardInfo, &.MuiAlert-standardSuccess, &.MuiAlert-standardWarning, &.MuiAlert-standardError':
      {
        color: 'text.primary',
        backgroundColor: 'background.paper',
      },
    '& .MuiButton-root': {
      color: 'primary.main',
    },
  },
  actionRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 0.5,
    color: 'inherit',
  },
  actionColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    flex: 1,
    color: 'inherit',
  },
  actionButton: {whiteSpace: 'nowrap', flexShrink: 0},
  closeButton: {m: 0},
  checkbox: {color: 'inherit', '&.Mui-checked': {color: 'inherit'}, p: 0, mr: 1},
  formControlLabel: {m: 0, mt: 1.5},
  body: {
    fontSize: '.875rem',
    lineHeight: 1.5,
    'p:first-of-type': {mt: 0},
    'p:last-of-type': {mb: 0},
  },
};

function SnackbarAlert({
  alertText,
  bodyHtml,
  itemStatus,
  onClose,
  action,
  actionLabel = 'Click to View',
  autoHideDuration,
  anchorOrigin = {vertical: 'top', horizontal: 'center'},
  variant = 'filled',
  sx,
  checkboxLabel,
  checkboxChecked,
  onCheckboxChange,
}: SnackbarAlertProps) {
  if (!itemStatus || (!alertText && !bodyHtml)) {
    return null;
  }
  const hasCheckbox = checkboxLabel !== undefined;
  const snackbarOnClose = (_evt: React.SyntheticEvent | Event, reason: SnackbarCloseReason) => {
    if (reason !== 'clickaway') {
      onClose();
    }
  };
  const actionButton = action && actionLabel && (
    <Button color="inherit" size="small" onClick={action} sx={styles.actionButton}>
      {actionLabel}
    </Button>
  );
  const closeButton = (
    <IconButton
      id="alert-close-button"
      ariaLabel="Close notification"
      color="inherit"
      onClick={onClose}
      icon={<Close fontSize="small" />}
      sx={styles.closeButton}
    />
  );
  return (
    <Snackbar
      anchorOrigin={anchorOrigin}
      key={alertText || bodyHtml}
      open={!!itemStatus}
      autoHideDuration={autoHideDuration}
      onClose={snackbarOnClose}
      sx={styles.snackbar}
    >
      <Alert
        sx={[
          styles.alert,
          variant === 'filled' && styles.alertFilled,
          variant === 'paper' && styles.alertPaper,
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        severity={itemStatus}
        iconMapping={{success: <CheckIcon fontSize="inherit" />}}
        variant={variant === 'paper' ? 'standard' : variant}
        action={
          hasCheckbox ? (
            // Close on top-right, CTA under it on bottom-right.
            <Box sx={styles.actionColumn}>
              {closeButton}
              {actionButton}
            </Box>
          ) : (
            // CTA on left, Close on right.
            <Box sx={styles.actionRow}>
              {actionButton}
              {closeButton}
            </Box>
          )
        }
      >
        {alertText && <Typography fontWeight="bold">{alertText}</Typography>}
        {bodyHtml && <Box sx={styles.body} dangerouslySetInnerHTML={{__html: bodyHtml}} />}
        {hasCheckbox && (
          <FormControlLabel
            sx={styles.formControlLabel}
            control={
              <Checkbox
                size="small"
                checked={!!checkboxChecked}
                onChange={(evt) => onCheckboxChange?.(evt.target.checked)}
                sx={styles.checkbox}
              />
            }
            label={<Typography variant="body2">{checkboxLabel}</Typography>}
          />
        )}
      </Alert>
    </Snackbar>
  );
}

export default SnackbarAlert;
